import { Controller } from '@hotwired/stimulus'
import { enter, leave, toggle } from 'el-transition'
import { get, post, put } from '@rails/request.js'
import { parseDigits, parsePhoneNumberFromString } from 'libphonenumber-js'
import { TelnyxRTC } from '@telnyx/webrtc'
import { Timer } from 'easytimer.js'

// Connects to data-controller="dialer"
export default class extends Controller {
  static targets = [
    'phoneInput', 'numberField', 'countrySelect', 'countryCodeDisplay',
    'callScreen', 'muteButton',
    'callingNumber', 'callStatus', 'rateAmount', 'callerName', 'recordCallToggle',
    'recordingStatus', 'callTimer', 'endCallButton', 'callerNumberSelect',
    'incomingPanel', 'incomingName', 'incomingNumber', 'incomingLine', 'fabPulse'
  ]

  static values = { enterpriseSignup: Boolean }

  connect() {
    setTimeout(() => {
      this.countrySelectTarget.value = 'US'
      this.countrySelectTarget.dispatchEvent(new Event('change'));
    }, 300)

    const selectedOption = this.countrySelectTarget.options[this.countrySelectTarget.selectedIndex]
    const display = selectedOption.dataset.display
    this.countryCodeDisplayTarget.textContent = display

    // Prevent any page reload/navigation during calls
    this.preventReload = (event) => {
      if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
        console.log('Preventing page reload during active call')
        event.preventDefault()
        event.stopImmediatePropagation()
        return false
      }
    }

    // Prevent browser reload/navigation
    this.beforeUnloadHandler = (event) => {
      if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
        event.preventDefault()
        event.returnValue = 'Call in progress. Are you sure you want to leave?'
        return 'Call in progress. Are you sure you want to leave?'
      }
    }

    // Prevent Turbo Drive reloads
    document.addEventListener('turbo:before-visit', this.preventReload)
    document.addEventListener('turbo:before-fetch-request', this.preventReload)

    // Prevent browser reloads
    window.addEventListener('beforeunload', this.beforeUnloadHandler)

    this.handleDialEvent = (e) => this.handleClickToCall(e.detail)
    window.addEventListener('zencall:dial', this.handleDialEvent)

    const base = '/dtmf/';
    this.tones = {
      '0': new Audio(`${base}dtmf-0.mp3`),
      '1': new Audio(`${base}dtmf-1.mp3`),
      '2': new Audio(`${base}dtmf-2.mp3`),
      '3': new Audio(`${base}dtmf-3.mp3`),
      '4': new Audio(`${base}dtmf-4.mp3`),
      '5': new Audio(`${base}dtmf-5.mp3`),
      '6': new Audio(`${base}dtmf-6.mp3`),
      '7': new Audio(`${base}dtmf-7.mp3`),
      '8': new Audio(`${base}dtmf-8.mp3`),
      '9': new Audio(`${base}dtmf-9.mp3`),
      '*': new Audio(`${base}dtmf-star.mp3`),
      '#': new Audio(`${base}dtmf-pound.mp3`),
      'ringing': new Audio(`${base}phone-ringing.mp3`),
      'hangup': new Audio(`${base}phone-hangup.mp3`)
    }

    this.callerNumber = this.element.dataset.phoneNumber
    this.callLog = null
    this.telnyxPhoneNumberId = this.element.dataset.phoneNumberId
    this.recordCall = false
    this.inboundNumbers = JSON.parse(this.element.dataset.inboundNumbers || '[]')
    this.incomingCall = null
    this.incomingTone = new Audio('/dtmf/phone-ringing.mp3')
    this.incomingTone.loop = true
    this.canEndCall = true
    this.maxCallSeconds = null

    if (this.hasCallerNumberSelectTarget) {
      this.applyCallerNumberSelection()
    }

    if (this.element.dataset.telnyxToken.length) {
      this.connectTelnyxClient()
    }
  }

  disconnect() {
    if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
      this.currentCall.hangup()
    }
    this.dismissIncomingCall()
    this.disconnectTelnyxClient()
    this.stopRingingTimer()
    this.stopCallTimer()

    document.removeEventListener('turbo:before-visit', this.preventReload)
    document.removeEventListener('turbo:before-fetch-request', this.preventReload)
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    window.removeEventListener('zencall:dial', this.handleDialEvent)
  }

  connectTelnyxClient() {
    const telnyxParams = {
      audio: true,
      video: false,
      logLevel: 'info'
    }

    const login = this.recordCall ? this.element.dataset.telnyxRecordUsername : this.element.dataset.telnyxUsername
    const password = this.recordCall ? this.element.dataset.telnyxRecordPassword : this.element.dataset.telnyxPassword

    this.loggedInWithToken = !login

    if (login) {
      telnyxParams['login'] = login
      telnyxParams['password'] = password
    } else {
      telnyxParams['login_token'] = this.recordCall ? this.element.dataset.telnyxRecordToken : this.element.dataset.telnyxToken
    }

    this.telnyxClient = new TelnyxRTC(telnyxParams)

    this.telnyxClient.on('telnyx.ready', () => {
      console.log('telnyx connected')
      this.tokenRefreshAttempted = false
    })

    this.telnyxClient.on('telnyx.error', (err) => {
      console.error('❌ Telnyx WebRTC error:', err);
      if (err?.error?.name === 'LOGIN_FAILED') this.reconnectWithFreshToken()
    });

    this.telnyxClient.on('telnyx.notification', (notification) => {
      const call = notification.call
      if (!call) return

      if (call.direction === 'inbound' && call !== this.currentCall) {
        if (notification.type === 'callUpdate') this.handleIncomingCallUpdate(call)
        return
      }

      if (call.state === 'active' && call.remoteStream) {
        console.log("🎧 Attaching remoteStream to audio element");

        if (!this.audioElement) {
          this.audioElement = new Audio();
          this.audioElement.autoplay = true;
          this.audioElement.hidden = true;
          document.body.appendChild(this.audioElement); // optional: add to DOM
        }

        this.audioElement.srcObject = call.remoteStream;

        this.audioElement
          .play()
          .then(() => console.log("🔊 Audio playing"))
          .catch((err) => console.error("❌ Audio play failed:", err));
      }

      if (notification.type === 'callUpdate') {
        const state = call.state
        console.log('Call state:', state)

        const labelMap = {
          new: 'Dialing...',
          requesting: 'Dialing...',
          trying: 'Ringing...',
          early: 'Ringing...',
          answering: 'Connecting...',
          active: 'Connected',
          hangup: 'Call Ended',
          destroy: 'Disconnected'
        }

        this.callStatusTarget.textContent = labelMap[state]

        if (this.currentCall && (state === 'requesting')) {
          this.tones['ringing'].play()
        }

        if (this.currentCall && state === 'active') {
          this.callConnected = true
          this.tones['ringing'].pause()
          this.stopRingingTimer()
          this.startCallTimer()
        }

        if (this.currentCall && state === 'hangup') {
          leave(this.callScreenTarget)
          this.element.classList.remove('call-active')
          this.tones['ringing'].pause()
          this.tones['hangup'].play()
          this.numberFieldTarget.value = ''
          this.isMuted = false
          this.muteButtonTarget.setAttribute('aria-pressed', false)
          this.stopRingingTimer()
          this.stopCallTimer()
          this.finishCall()
        }

        if (state === 'destroy') {
          this.tones['ringing'].pause()
          this.tones['hangup'].pause()
          this.stopRingingTimer()
          this.currentCall = null
        }

        if (state === 'ringing') {
          // console.log('inside ringing')
          // console.log('notification: ', notification)
          // call.answer()
        }
      }
    })

    this.telnyxClient.connect()
  }

  async reconnectWithFreshToken() {
    if (!this.loggedInWithToken || this.tokenRefreshAttempted) return
    this.tokenRefreshAttempted = true

    const response = await put('/telnyx_token', {
      body: { kind: this.recordCall ? 'record' : 'regular' },
      responseKind: 'json'
    })
    if (!response.ok) return

    const { token } = await response.json
    if (!token) return

    if (this.recordCall) {
      this.element.dataset.telnyxRecordToken = token
    } else {
      this.element.dataset.telnyxToken = token
    }
    this.disconnectTelnyxClient()
    this.connectTelnyxClient()
  }

  disconnectTelnyxClient() {
    if (this.telnyxClient) {
      this.telnyxClient.disconnect()
      this.telnyxClient.off('telnyx.ready')
      this.telnyxClient.off('telnyx.error')
      this.telnyxClient.off('telnyx.notification')
      this.telnyxClient = null
    }
  }

  handleIncomingCallUpdate(call) {
    if (call.state === 'ringing') {
      this.offerIncomingCall(call)
      return
    }

    if (call === this.incomingCall && (call.state === 'hangup' || call.state === 'destroy')) {
      this.dismissIncomingCall()
    }
  }

  async offerIncomingCall(call) {
    if (this.incomingCall) return
    if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') return
    if (!this.inboundNumbers.includes(call.options.callerNumber)) return

    this.incomingCall = call
    const caller = call.options.remoteCallerNumber
    const dialed = call.options.callerNumber
    this.incomingNameTarget.textContent = ''
    this.incomingNumberTarget.textContent = parsePhoneNumberFromString(caller)?.formatInternational() || caller
    this.incomingLineTarget.textContent = `Incoming call on ${parsePhoneNumberFromString(dialed)?.formatInternational() || dialed}`
    this.incomingPanelTarget.classList.remove('hidden')
    if (this.hasFabPulseTarget) this.fabPulseTarget.classList.remove('hidden')
    this.application.getControllerForElementAndIdentifier(this.element, 'dialer-popup')?.expandPopup()
    this.incomingTone.play().catch(() => {})

    await this.lookupCaller(call, caller)
    if (this.incomingCall === call) this.notifyIncomingCall(caller)
  }

  async lookupCaller(call, caller) {
    const response = await get(`/contacts/lookup?phone_number=${encodeURIComponent(caller)}`, { responseKind: 'json' })
    if (!response.ok || this.incomingCall !== call) return

    const body = await response.json
    if (body.name) this.incomingNameTarget.textContent = body.name
  }

  notifyIncomingCall(caller) {
    if (!document.hidden || !('Notification' in window) || Notification.permission !== 'granted') return

    const notification = new Notification('Incoming call', { body: this.incomingNameTarget.textContent || caller })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  }

  async acceptCall(e) {
    e.preventDefault()

    const call = this.incomingCall
    if (!call) return

    this.dismissIncomingCall()
    this.currentCall = call
    this.callConnected = false
    this.maxCallSeconds = null
    this.contactId = null
    this.dialSessionId = null
    this.callLog = null
    this.callingNumberTarget.textContent = this.incomingNameTarget.textContent || this.incomingNumberTarget.textContent
    this.callingNumberTarget.dataset.userCallScreenInput = 'false'
    this.callStatusTarget.textContent = 'Connecting...'
    enter(this.callScreenTarget)
    this.element.classList.add('call-active')

    await call.answer()

    const response = await put('/call_logs/answer', {
      body: { telnyx_call_session_id: call.telnyxIDs.telnyxSessionId },
      responseKind: 'json'
    })

    if (response.ok) {
      const body = await response.json
      this.callLog = body['data']
    }
  }

  declineCall(e) {
    e.preventDefault()

    const call = this.incomingCall
    this.dismissIncomingCall()
    if (!call) return

    call.hangup()
    put('/call_logs/decline', {
      body: { telnyx_call_session_id: call.telnyxIDs.telnyxSessionId },
      responseKind: 'json'
    })
  }

  dismissIncomingCall() {
    this.incomingCall = null
    if (this.incomingTone) {
      this.incomingTone.pause()
      this.incomingTone.currentTime = 0
    }
    if (this.hasIncomingPanelTarget) this.incomingPanelTarget.classList.add('hidden')
    if (this.hasFabPulseTarget) this.fabPulseTarget.classList.add('hidden')
  }

  normalizePhoneNumber(raw) {
    const digits = raw.replace(/[^\d]/g, '');

    // Check if it starts with "1" for US/Canada numbers
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return raw; // fallback for unexpected formats
  }

  async selectPhoneNumber() {
    await this.applyCallerNumberSelection()

    this.disconnectTelnyxClient()
    this.connectTelnyxClient()
  }

  async applyCallerNumberSelection() {
    const select = this.callerNumberSelectTarget
    const selectedOption = select.options[select.selectedIndex]

    if (selectedOption.value === 'public') {
      this.callerNumber = this.normalizePhoneNumber(this.element.dataset.phoneNumber)
      this.telnyxPhoneNumberId = this.element.dataset.phoneNumberId
      return
    }

    this.callerNumber = this.normalizePhoneNumber(selectedOption.textContent)
    this.telnyxPhoneNumberId = selectedOption.value

    const response = await get(`/buy_numbers/${selectedOption.value}`, {
      contentType: 'application/json',
      responseKind: 'json'
    })

    if (response.ok) {
      const jsonResponse = await response.json
      this.callerNameTarget.value = jsonResponse.caller_name
    }
  }

  input(e) {
    e.preventDefault()

    let currentNumber = this.numberFieldTarget.value
    currentNumber += e.target.dataset.number

    this.tones[e.target.dataset.number].play()

    this.numberFieldTarget.value = currentNumber
  }

  keyboardInput(e) {
    e.preventDefault()

    const number = this.numberFieldTarget.value

    if (number.length > 5) {
      this.fetchRate()
    } else {
      this.rateAmountTarget.textContent = "$— / min"
    }

    if (e.key === 'Enter') {
      this.authorizeCall()
    }
  }

  fetchRate() {
    const number = parseDigits(this.numberFieldTarget.value)

    this.rateAmountTarget.classList.remove('text-danger')

    if (number.length <= 5) {
      this.rateAmountTarget.textContent = "$— / min"
      return
    }

    const originNumber = this.callerNumber
    const selectedCountry = this.countrySelectTarget.options[this.countrySelectTarget.selectedIndex]
    const countryCode = `+${selectedCountry.dataset.countryCode}`
    const destinationNumber = `+${countryCode}${number}`

    const country = this.countrySelectTarget.value

    get(`/rate_matcher?origin=${encodeURIComponent(originNumber)}&destination=${encodeURIComponent(destinationNumber)}&record_call=${this.recordCall}&country=${encodeURIComponent(country)}&organization_id=${encodeURIComponent(this.element.dataset.organizationId || '')}`, {
      responseKind: 'json'
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json
        if (data.price) {
          this.rateAmountTarget.textContent = `$${data.price} / min`
        } else {
          this.rateAmountTarget.textContent = '$— / min'
        }
      } else {
        this.rateAmountTarget.textContent = '$— / min'
      }
    }).catch(() => {
      this.rateAmountTarget.textContent = '$— / min'
    })
  }

  handleClickToCall({ phoneNumber, countryCode, contactId, dialSessionId, autoDial }) {
    this.contactId = contactId || null
    this.dialSessionId = dialSessionId || null

    let dialCode = null
    if (countryCode) {
      for (const opt of this.countrySelectTarget.options) {
        if (opt.value === countryCode) {
          opt.selected = true
          dialCode = opt.dataset.countryCode
          this.updateCountryDisplayFromSelect()
          break
        }
      }
    }

    if (phoneNumber) {
      let local = phoneNumber
      if (dialCode) {
        const prefix = `+${dialCode}`
        if (local.startsWith(prefix)) local = local.slice(prefix.length)
      }
      this.numberFieldTarget.value = local.replace(/[^\d]/g, '')
    }

    const popup = this.application.getControllerForElementAndIdentifier(this.element, 'dialer-popup')
    popup?.expandPopup()

    this.fetchRate()

    if (autoDial) {
      this.authorizeCall()
    }
  }

  selectContact(e) {
    const fullNumber = e.detail.phoneNumber;
    const countryCode = e.detail.countryCode;

    if (!fullNumber || !countryCode) return;

    let dialCode = null;
    for (const opt of this.countrySelectTarget.options) {
      if (opt.value === countryCode) {
        opt.selected = true;
        dialCode = opt.dataset.countryCode;
        this.updateCountryDisplayFromSelect();
        break;
      }
    }

    const prefix = dialCode ? `+${dialCode}` : '';
    const localNumber = prefix && fullNumber.startsWith(prefix) ? fullNumber.slice(prefix.length).replace(/^\-/, '') : fullNumber;
    this.numberFieldTarget.value = localNumber;
  }

  updateCountryDisplayFromSelect() {
    const selectedOption = this.countrySelectTarget.options[this.countrySelectTarget.selectedIndex];
    const display = selectedOption.dataset.display;
    this.countryCodeDisplayTarget.textContent = display;
  }

  changeCountry(e) {
    if (e) {
      e.preventDefault()
    }

    this.fetchRate()

    this.updateCountryDisplayFromSelect()
  }

  delete(e) {
    e.preventDefault()

    this.numberFieldTarget.value = this.numberFieldTarget.value.slice(0, -1)
  }

  phoneNumber() {
    return `${this.countryCodeDisplayTarget.textContent.slice(5)}${parseDigits(this.numberFieldTarget.value)}`
  }

  async toggleRecording(e) {
    e.preventDefault()

    if (e.target.dataset.checked === 'true') {
      e.target.dataset.checked = 'false'
      e.target.classList.remove('bg-primary')
      e.target.classList.add('bg-hairline')
      this.recordCallToggleTarget.classList.remove('translate-x-5')
      this.recordCallToggleTarget.classList.add('translate-x-0')
    } else {
      e.target.dataset.checked = 'true'
      e.target.classList.add('bg-primary')
      e.target.classList.remove('bg-hairline')
      this.recordCallToggleTarget.classList.add('translate-x-5')
      this.recordCallToggleTarget.classList.remove('translate-x-0')
    }

    this.recordCall = e.target.dataset.checked === 'true'
    const response = await put(`/phone_number_sip_assignments/${this.telnyxPhoneNumberId}`, {
      body: {
        record_call: this.recordCall
      },
      responseKind: 'json'
    })

    if (response.ok) {
      this.disconnectTelnyxClient()
      this.connectTelnyxClient()
      this.fetchRate()

      if (this.recordCall) {
        this.recordingStatusTarget.classList.remove('hidden')
      } else {
        this.recordingStatusTarget.classList.add('hidden')
      }
    }
  }

  async createCallLog(destinationNumber, telnyxId, direction) {
    const response = await post('/call_logs', {
      body: {
        phone_number: destinationNumber,
        telnyx_id: this.currentCall.id,
        direction: direction,
        organization_id: this.element.dataset.organizationId,
        contact_id: this.contactId,
        dial_session_id: this.dialSessionId
      },
      responseKind: 'json'
    })

    if (response.ok) {
      const body = await response.json
      this.callLog = body['data']
      window.dispatchEvent(new CustomEvent('zencall:call-started', { detail: { callLogId: this.callLog?.id } }))
    } else if (this.currentCall) {
      this.currentCall.hangup()
    }
  }

  async authorizeCall() {
    const separator = this.element.dataset.authorizeUrl.includes('?') ? '&' : '?'
    const country = this.countrySelectTarget.value
    const originNumber = this.callerNumber
    const selectedCountry = this.countrySelectTarget.options[this.countrySelectTarget.selectedIndex]
    const countryCode = `+${selectedCountry.dataset.countryCode}`
    const destinationNumber = `${countryCode}${parseDigits(this.numberFieldTarget.value)}`
    const authorizeUrl = `${this.element.dataset.authorizeUrl}${separator}record_call=${this.recordCall}&country=${encodeURIComponent(country)}&origin=${encodeURIComponent(originNumber)}&destination=${encodeURIComponent(destinationNumber)}`

    const response = await get(authorizeUrl, {
      responseKind: 'json'
    })

    if (response.ok) {
      const body = await response.json

      if (body['success']) {
        this.maxCallSeconds = body['max_call_seconds']
        this.call()
      } else {
        if (body['reason'] === 'signup') {
          this.signupModal()
          return
        } else if (body['reason'] === 'credits') {
          this.purchaseCreditsModal()
          return
        } else if (body['reason'] === 'call_in_progress') {
          this.rateAmountTarget.textContent = 'A call is already in progress'
          this.rateAmountTarget.classList.add('text-danger')
          return
        } else if (body['reason'] === 'unauthorized') {
          this.rateAmountTarget.textContent = 'Call not authorized'
          this.rateAmountTarget.classList.add('text-danger')
          return
        }
      }
    }
  }

  safeStringify(obj) {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    }, 2);
  }

  call(e) {
    if (e) {
      e.preventDefault()
    }

    if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
      console.log("⚠️ A call is already in progress. Aborting new call.");
      return;
    }

    enter(this.callScreenTarget)
    this.element.classList.add('call-active')

    const destinationNumber = this.phoneNumber()

    this.callingNumberTarget.textContent = parsePhoneNumberFromString(destinationNumber)?.formatInternational() || destinationNumber

    const newCallParams = {
      destinationNumber: destinationNumber,
      callerNumber: this.callerNumber,
    }

    if (this.callerNameTarget.value.trim().length > 0) {
      newCallParams['callerName'] = this.callerNameTarget.value.trim()
    }

    this.currentCall = this.telnyxClient.newCall(newCallParams)
    this.callConnected = false

    this.canEndCall = false
    this.disableEndCallButton()
    this.startRingingTimer()

    this.createCallLog(destinationNumber, this.currentCall.id, 'outbound')
  }

  endCall(e) {
    if (e) {
      e.preventDefault()
    }

    if (!this.canEndCall) {
      console.log('⚠️ hangup() blocked: minimum call duration not reached')
      return
    }

    if (
      this.currentCall &&
      this.currentCall.state !== 'hangup' &&
      this.currentCall.state !== 'destroy'
    ) {
      console.log('📞 Calling hangup()');
      this.currentCall.hangup();
    } else {
      console.log('⚠️ hangup() skipped: call is already ended');
    }
  }

  mute(e) {
    e.preventDefault()

    if (!this.currentCall) return

    if (this.isMuted) {
      this.currentCall.unmuteAudio()
      this.isMuted = false
    } else {
      this.currentCall.muteAudio()
      this.isMuted = true
    }
    this.muteButtonTarget.setAttribute('aria-pressed', this.isMuted)
  }

  callScreenInput(e) {
    e.preventDefault()

    this.tones[e.target.dataset.number].play()

    if (this.callingNumberTarget.dataset.userCallScreenInput === 'false') {
      this.callingNumberTarget.textContent = e.target.dataset.number
      this.callingNumberTarget.dataset.userCallScreenInput = 'true'
    } else {
      this.callingNumberTarget.textContent += e.target.dataset.number
    }

    this.currentCall.dtmf(e.target.dataset.number)
  }

  async signupModal() {
    const url = this.enterpriseSignupValue ? '/signup_modal?enterprise_signup=true' : '/signup_modal'
    await get(url, {
      responseKind: 'turbo_stream'
    })
  }

  async purchaseCreditsModal() {
    await get('/purchase_credits_modal', {
      responseKind: 'turbo_stream'
    })
  }

  async recordHangup() {
    if (!this.callLog || !this.callLog.id) return

    await put(`/call_logs/${this.callLog.id}/hangup`, { responseKind: 'json' })
  }

  async finishCall() {
    await this.recordHangup()

    if (this.dialSessionId) {
      window.dispatchEvent(new CustomEvent('zencall:call-ended', {
        detail: { callLogId: this.callLog?.id, connected: this.callConnected === true }
      }))
      this.dialSessionId = null
    }

    this.contactId = null
    this.callLog = null
  }

  startRingingTimer() {
    this.ringingTimer = new Timer()

    this.ringingTimer.addEventListener('secondsUpdated', () => {
      if (this.ringingTimer.getTotalTimeValues().seconds >= 10 && !this.canEndCall) {
        console.log('🔔 Ringing timer reached 10 seconds - enabling hang-up')
        this.canEndCall = true
        this.enableEndCallButton()
      }
    })

    this.ringingTimer.start()
    console.log('🔔 Ringing timer started')
  }

  stopRingingTimer() {
    if (this.ringingTimer) {
      this.ringingTimer.stop()
      this.ringingTimer = null
      console.log('🔔 Ringing timer stopped')
    }
  }

  startCallTimer() {
    this.callTimer = new Timer()
    this.canEndCall = false
    this.disableEndCallButton()

    this.callTimer.addEventListener('secondsUpdated', () => {
      this.callTimerTarget.textContent = this.callTimer.getTimeValues().toString(['minutes', 'seconds'])

      if (this.callTimer.getTotalTimeValues().seconds >= 8 && !this.canEndCall) {
        this.canEndCall = true
        this.enableEndCallButton()
      }

      if (this.maxCallSeconds && this.callTimer.getTotalTimeValues().seconds >= this.maxCallSeconds) {
        console.log('Credit limit reached - ending call')
        this.canEndCall = true
        this.endCall()
        this.purchaseCreditsModal()
      }
    })

    this.callTimer.start()
    this.callTimerTarget.classList.remove('hidden')
  }

  stopCallTimer() {
    if (this.callTimer) {
      this.callTimer.stop()
      this.callTimer = null
    }
    this.callTimerTarget.classList.add('hidden')
    this.callTimerTarget.textContent = '00:00'
    this.canEndCall = true
    this.enableEndCallButton()
  }

  disableEndCallButton() {
    this.endCallButtonTarget.classList.remove('bg-danger', 'hover:bg-danger-hover', 'cursor-pointer')
    this.endCallButtonTarget.classList.add('bg-danger/40', 'cursor-not-allowed')
  }

  enableEndCallButton() {
    this.endCallButtonTarget.classList.remove('bg-danger/40', 'cursor-not-allowed')
    this.endCallButtonTarget.classList.add('bg-danger', 'hover:bg-danger-hover', 'cursor-pointer')
  }
}
